import { rgb } from "d3-color";
import React, { useMemo } from "react";
import InternalDebuggerStateValueView from "./InternalDebuggerStateValueView";
import "./InternalDebuggerElementView.scss";
import type { Release } from "@/types/interfaces.rulegraph";

export interface DebuggerElementViewProps {
  value: any;
  graph?: any;
  release: Release;
  depth?: number;
}

const PASTEL_COLORS = ["#BAE1FF", "#BFFFBF", "#FFFFBA", "#FFDFBA", "#FFB3BA", "#FFBAF3"];

const DebuggerElementView = (props: DebuggerElementViewProps) => {
  const { value, graph, depth = 0, release } = props;

  if (!value) {
    return <span>null</span>;
  }

  if (typeof value === "string") {
    return <span>{value}</span>;
  }

  if (typeof value === "number") {
    return <span>{value}</span>;
  }

  if (typeof value === "boolean") {
    return <span>{value.toString()}</span>;
  }

  // Handle complex objects with type information (similar to original ElementView)
  if (value.type) {
    const relationship =
      value.relationshipId &&
      release?.relationships?.find((relationship: any) => relationship.id === value.relationshipId);
    const properties = {
      scope: (value.scope?.entity ?? value.scope) || undefined,
      relationshipReverse: value.relationshipReverse || undefined,
      relationshipId: value.relationshipId || undefined,
      relationship: relationship?.text,
    };

    const attributeId = value.attributeId;

    const attributeNodes = useMemo(() => {
      const nodes =
        attributeId && graph?.nodes?.filter((node: any) => node.v.endsWith(attributeId) && !node.value.hidden);
      if (nodes?.length) {
        return nodes;
      }
      return undefined;
    }, [graph, attributeId]);

    const propertyCount = Object.values(properties).filter((prop) => prop !== undefined).length;

    let content = (
      <>
        {value.type}
        {value.type === "value" && (
          <span>({value.special ? <i>{value.special}</i> : JSON.stringify(value.value)})</span>
        )}
        {propertyCount > 0 && <span>[{JSON.stringify(properties)}]</span>}
        {attributeId && (
          <small>({[attributeNodes?.[0]?.value.description, attributeId].filter(Boolean).join(" / ")})</small>
        )}
      </>
    );

    if (value.negate) {
      content = <>negate({content})</>;
    }

    // Calculate background color using d3-color for lightening
    const baseColor = PASTEL_COLORS[depth % PASTEL_COLORS.length];
    const backgroundColor = rgb(baseColor).brighter(0.05).toString();

    return (
      <div
        className="ElementView"
        style={{ backgroundColor }}
      >
        {content}
        {attributeNodes
          ? attributeNodes.map((node: any) => (
              <React.Fragment key={node.v}>
                <InternalDebuggerStateValueView state={node} />
              </React.Fragment>
            ))
          : null}
        {value.elements?.map((element: any, index: number) => (
          <DebuggerElementView
            release={release}
            graph={graph}
            key={index}
            value={element}
            depth={depth + 1}
          />
        ))}
      </div>
    );
  }

  // Fallback for other complex objects
  return (
    <div className="ElementView">
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  );
};

export default DebuggerElementView;
